"use client";

import * as z from "zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useBoolean } from "minimal-shared/hooks";
import { useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useRouter } from "src/routes/hooks";
import { RouterLink } from "src/routes/components";

import { Iconify } from "src/components/iconify";
import { Field, Form, schemaUtils } from "src/components/hook-form";
import { getErrorMessage } from "../../utils";
import { FormHead } from "../../components/form-head";
import { FormDivider } from "../../components/form-divider";
import { RegistreraKontoSektion } from "../../components/registrera-konto-sektion";
import {
    signInWithFacebook,
    signInWithGoogle,
    signInWithPassword,
} from "../../context/firebase";
import { paths } from "@/src/routes/paths";
import { toast } from "src/components/snackbar";
import { hämtaAnvändare } from "src/sections/anvandare/AnvandarService";
// import { getAuthenticatedAppForUser } from "@/src/lib/firebase/firebaseInstans";

// ----------------------------------------------------------------------

export type SignInSchemaType = z.infer<typeof SignInSchema>;

export const SignInSchema = z.object({
    email: schemaUtils.email(),
    password: z
        .string()
        .min(1, { message: "Password is required!" })
        .min(6, { message: "Password must be at least 6 characters!" }),
});

// ----------------------------------------------------------------------

export function InloggningView() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const redirectPath = searchParams.get("returnTo");
    const isVerified = searchParams.get("verified") === "true";
    const initialEmail = searchParams.get("email") || "";
    const showPassword = useBoolean();

    // const { checkUserSession } = useAuthContext();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // const createRedirectPath = (query: string) => {
    //   const queryString = new URLSearchParams({ returnTo: query }).toString();
    //   return `${paths.auth.firebase.verify}?${queryString}`;
    // };

    const defaultValues: SignInSchemaType = {
        email: initialEmail,
        password: "",
    };


    const methods = useForm({
        resolver: zodResolver(SignInSchema),
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const omdirigeringsväg = async (fallback: string): Promise<string> => {
        try {
            const användare = await hämtaAnvändare();
            if (!användare.harGenomgåttOnboarding) {
                return paths.ONBOARDING;
            }
        } catch {
            // Kan inte avgöra — låt AuthGuard hantera det
        }
        return fallback;
    };

    const onSubmit = handleSubmit(async (data) => {
        try {
            await signInWithPassword({ email: data.email, password: data.password });

            const mål = await omdirigeringsväg(redirectPath || paths.dashboard.root);
            router.push(mål);
        } catch (error) {
            console.error(error);
            const feedbackMessage = getErrorMessage(error);
            setErrorMessage(feedbackMessage);
        }
    });

    const handleSignInWithGoogle = async () => {
        try {
            await signInWithGoogle();
            const mål = await omdirigeringsväg(redirectPath || paths.dashboard.root);
            router.push(mål);
            toast.success('Inloggad');
        } catch (error) {
            console.error(error);
            toast.error((error as any).message);
        }
    };

    const handleSignInWithFacebook = async () => {
        try {
            await signInWithFacebook();
            const mål = await omdirigeringsväg(redirectPath || paths.dashboard.root);
            router.push(mål);
            toast.success('Inloggad');
        } catch (error) {
            console.error(error);
            toast.error((error as any).message);
        }
    };

    const renderForm = () => (
        <Box sx={{ gap: 3, display: "flex", flexDirection: "column" }}>
            {/*<AuthHeaderHandler initialUser={currentUser?.toJSON()} />*/}
            <Field.Text
                name="email"
                label="Email"
                slotProps={{ inputLabel: { shrink: true } }}
            />

            <Box sx={{ gap: 1.5, display: "flex", flexDirection: "column" }}>
                <Link
                    component={RouterLink}
                    href={methods.watch('email') ? `${paths.ÅTERSTÄLL}?email=${encodeURIComponent(methods.watch('email'))}` : paths.ÅTERSTÄLL}
                    variant="body2"
                    color="inherit"
                    sx={{ alignSelf: "flex-end" }}
                >
                    Glömt lösenordet?
                </Link>

                <Field.Text
                    name="password"
                    label="Lösenord"
                    placeholder="6+ tecken"
                    type={showPassword.value ? "text" : "password"}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={showPassword.onToggle}
                                        edge="end"
                                        aria-label={showPassword.value ? "Dölj lösenord" : "Visa lösenord"}
                                    >
                                        <Iconify
                                            icon={
                                                showPassword.value
                                                    ? "solar:eye-bold"
                                                    : "solar:eye-closed-bold"
                                            }
                                        />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>

            <Button
                fullWidth
                color="inherit"
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                loadingIndicator="Sign in..."
            >
                Logga in
            </Button>
        </Box>
    );

    return (
        <>
            <FormHead
                title="Logga in"
                sx={{ textAlign: { xs: "center", md: "left" } }}
            />

            {isVerified && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Din email har verifierats! Du kan nu logga in.
                </Alert>
            )}

            {!!errorMessage && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {errorMessage}
                </Alert>
            )}


            <Form methods={methods} onSubmit={onSubmit}>
                {renderForm()}
            </Form>

            <FormDivider />

            <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
                <Button
                    fullWidth
                    size="large"
                    color="inherit"
                    variant="outlined"
                    onClick={handleSignInWithGoogle}
                    startIcon={<Iconify icon="logos:google-icon" />}
                >
                    Logga in med Google
                </Button>

                <Button
                    fullWidth
                    size="large"
                    color="inherit"
                    variant="outlined"
                    onClick={handleSignInWithFacebook}
                    startIcon={<Iconify icon="socials:facebook" />}
                >
                    Logga in med Facebook
                </Button>
            </Box>

            <RegistreraKontoSektion sx={{ mt: 4 }} />
        </>
    );
}
